// feedback-modal (web #1) — minimal "Report a problem" modal. Captures a
// PostHog `$feedback` event (same event the desktop app emits, so web reports
// land in the same alpha-feed digest) instead of opening a GitHub issue.

import { t } from './i18n.js';

type Capture = (event: string, props: Record<string, unknown>) => void;

export interface FeedbackModalDeps {
  /** Injected in tests; defaults to window.posthog.capture. */
  capture?: Capture;
}

export interface FeedbackModalHandle {
  close: () => void;
}

function defaultCapture(event: string, props: Record<string, unknown>): void {
  (window as unknown as { posthog?: { capture: Capture } }).posthog?.capture(event, props);
}

export function openFeedbackModal(deps: FeedbackModalDeps = {}): FeedbackModalHandle {
  const capture = deps.capture ?? defaultCapture;

  const overlay = document.createElement('div');
  overlay.className = 'fb-overlay';
  overlay.setAttribute('data-role', 'feedback-overlay');

  const panel = document.createElement('div');
  panel.className = 'fb-panel';

  const title = document.createElement('h2');
  title.className = 'fb-title';
  title.textContent = t('feedback.title');

  const ta = document.createElement('textarea');
  ta.className = 'fb-text';
  ta.setAttribute('data-role', 'feedback-text');
  ta.placeholder = t('feedback.placeholder');
  ta.rows = 5;

  const actions = document.createElement('div');
  actions.className = 'fb-actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'fb-cancel';
  cancel.textContent = t('feedback.cancel');

  const submit = document.createElement('button');
  submit.type = 'button';
  submit.className = 'fb-submit';
  submit.setAttribute('data-role', 'feedback-submit');
  submit.textContent = t('feedback.submit');

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    try { overlay.remove(); } catch { /* noop */ }
  };

  cancel.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  ta.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

  submit.addEventListener('click', () => {
    const text = ta.value.trim();
    if (!text) { ta.focus(); return; }
    capture('$feedback', {
      $feedback: text,
      description: text,
      platform: 'web',
      app: 'share-web',
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
    // Acknowledge inline, then auto-close.
    panel.textContent = '';
    const thanks = document.createElement('p');
    thanks.className = 'fb-thanks';
    thanks.setAttribute('data-role', 'feedback-thanks');
    thanks.textContent = t('feedback.thanks');
    panel.appendChild(thanks);
    setTimeout(close, 1500);
  });

  actions.appendChild(cancel);
  actions.appendChild(submit);
  panel.appendChild(title);
  panel.appendChild(ta);
  panel.appendChild(actions);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  ta.focus();

  return { close };
}
